import React, { useMemo, useState } from "react";
import { Box, Chip, Collapse, IconButton, Tooltip, Typography, useTheme } from "@mui/material";
import { alpha } from "@mui/material/styles";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import OutboxIcon from "@mui/icons-material/Outbox";
import AutoModeIcon from "@mui/icons-material/AutoMode";
import ScheduleIcon from "@mui/icons-material/Schedule";
import CheckIcon from "@mui/icons-material/Check";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import { getOutboundEmailDetails, splitStoredEmail } from "./outboundEmail";
import { plainEmailText } from "../emailCompose";
import {
  getDeliveryStatusDisplay,
  getFriendlyDeliveryError,
} from "../messageDeliveryStatus";

/** Chip de status reaproveitando a mesma leitura de entrega dos demais canais. */
const DeliveryChip = ({ delivery, theme }) => {
  if (!delivery?.label) return null;

  // "Enviado" é aceite do provedor, ainda não entrega: check simples e cor neutra
  // para não prometer o que só o evento de entrega confirma.
  const aguardandoEntrega = delivery.isPending || delivery.icon === "sent";

  const tone = delivery.isError
    ? theme.palette.error.main
    : aguardandoEntrega
      ? theme.palette.text.secondary
      : theme.palette.success.main;

  const Icon = delivery.isError
    ? ErrorOutlineIcon
    : delivery.isPending
      ? ScheduleIcon
      : delivery.icon === "sent"
        ? CheckIcon
        : DoneAllIcon;

  return (
    <Chip
      size="small"
      icon={<Icon sx={{ fontSize: 14 }} />}
      label={delivery.label}
      sx={{
        height: 22,
        flexShrink: 0,
        fontSize: "0.7rem",
        fontWeight: 600,
        color: tone,
        bgcolor: alpha(tone, 0.1),
        border: `1px solid ${alpha(tone, 0.25)}`,
        "& .MuiChip-icon": { color: tone, ml: 0.5 },
      }}
    />
  );
};

const AddressRow = ({ label, value }) => {
  if (!value) return null;
  return (
    <Box sx={{ display: "flex", gap: 0.75, alignItems: "baseline", minWidth: 0 }}>
      <Typography
        variant="caption"
        sx={{ color: "text.secondary", fontWeight: 600, flexShrink: 0, fontSize: "0.72rem" }}
      >
        {label}
      </Typography>
      <Typography
        variant="caption"
        sx={{
          color: "text.primary",
          fontSize: "0.72rem",
          wordBreak: "break-word",
          minWidth: 0,
        }}
      >
        {value}
      </Typography>
    </Box>
  );
};

/**
 * Card de e-mail enviado pela plataforma. Mostra quem recebeu, quem assinou e
 * qual regra disparou — o card de e-mail recebido não serve aqui porque exibe
 * apenas "De:" e sugere que a mensagem veio do cliente.
 */
const OutboundEmailCard = ({ message, children }) => {
  const theme = useTheme();
  const [expanded, setExpanded] = useState(false);

  const details = useMemo(() => getOutboundEmailDetails(message), [message]);
  const stored = useMemo(() => splitStoredEmail(message?.text), [message?.text]);

  const subject = details?.subject || stored.subject;
  const preview = plainEmailText(stored.body).replace(/\s+/g, " ").trim();
  const rule = details?.automation;
  const ccList = details?.cc?.length ? details.cc.join(", ") : null;
  const attachments = Array.isArray(details?.attachments) ? details.attachments : [];

  const delivery = useMemo(() => {
    const ds = getDeliveryStatusDisplay(message);
    if (message?.status === "error" || ds.isError) {
      return {
        ...ds,
        label: ds.label && ds.label !== "Enviado" ? ds.label : "Falha no envio",
        icon: "error",
        isError: true,
        isPending: false,
        errorReason:
          ds.errorReason ||
          message?.errorReason ||
          (typeof message?.error === "string" ? message.error : null),
      };
    }
    if (ds.label) return ds;
    return {
      label: "Enviado",
      icon: "sent",
      errorReason: null,
      isPending: false,
      isError: false,
    };
  }, [message]);
  const failureReason = delivery?.errorReason
    ? getFriendlyDeliveryError(String(delivery.errorReason)) || String(delivery.errorReason)
    : null;

  const accent = theme.palette.primary.main;

  return (
    <Box
      sx={{
        border: `1px solid ${theme.palette.divider}`,
        borderLeft: `3px solid ${accent}`,
        borderRadius: 2,
        overflow: "hidden",
        bgcolor: theme.palette.background.paper,
        // A bolha do atendente usa texto claro; sem fixar aqui o corpo expandido
        // herdaria essa cor e sumiria sobre o fundo branco do card.
        color: theme.palette.text.primary,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          gap: 1,
          px: 1.5,
          py: 1.25,
          bgcolor:
            theme.palette.mode === "dark" ? theme.palette.grey[900] : theme.palette.grey[50],
        }}
      >
        <OutboxIcon sx={{ fontSize: 20, mt: 0.25, color: accent }} />

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
            <Typography
              variant="caption"
              sx={{
                color: "text.secondary",
                fontSize: "0.68rem",
                textTransform: "uppercase",
                letterSpacing: 0.4,
                fontWeight: 700,
              }}
            >
              E-mail enviado
            </Typography>
            <DeliveryChip delivery={delivery} theme={theme} />
          </Box>

          {subject && (
            <Typography variant="subtitle2" fontWeight={700} sx={{ mt: 0.25, mb: 0.5 }}>
              {subject}
            </Typography>
          )}

          {rule && (
            <Tooltip
              title={
                rule.ruleName
                  ? `Disparado pela regra de automação "${rule.ruleName}"`
                  : "Disparado por uma regra de automação"
              }
            >
              <Chip
                size="small"
                icon={<AutoModeIcon sx={{ fontSize: 14 }} />}
                label={rule.ruleName ? `Automação · ${rule.ruleName}` : "Enviado por automação"}
                sx={{
                  height: 22,
                  mb: 0.75,
                  maxWidth: "100%",
                  fontSize: "0.7rem",
                  fontWeight: 600,
                  color: accent,
                  bgcolor: alpha(accent, 0.1),
                  border: `1px solid ${alpha(accent, 0.25)}`,
                  "& .MuiChip-icon": { color: accent, ml: 0.5 },
                }}
              />
            </Tooltip>
          )}

          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
            <AddressRow label="Para:" value={details?.to} />
            <AddressRow label="Cc:" value={ccList} />
            <AddressRow label="De:" value={details?.from} />
          </Box>
        </Box>

        <Tooltip title={expanded ? "Recolher e-mail" : "Ver e-mail completo"}>
          <IconButton size="small" onClick={() => setExpanded((v) => !v)}>
            {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </Tooltip>
      </Box>

      {failureReason && (
        <Box
          sx={{
            display: "flex",
            gap: 0.75,
            alignItems: "flex-start",
            px: 1.5,
            py: 1,
            bgcolor: alpha(theme.palette.error.main, 0.08),
            borderTop: `1px solid ${alpha(theme.palette.error.main, 0.2)}`,
          }}
        >
          <ErrorOutlineIcon sx={{ fontSize: 15, mt: "1px", color: theme.palette.error.main }} />
          <Typography variant="caption" sx={{ color: "text.secondary", fontSize: "0.72rem", lineHeight: 1.4 }}>
            {failureReason}
          </Typography>
        </Box>
      )}

      <Collapse in={expanded}>
        <Box sx={{ px: 1.5, py: 1.25, borderTop: `1px solid ${theme.palette.divider}` }}>
          {children}
        </Box>
      </Collapse>

      {!expanded && preview && (
        <Box sx={{ px: 1.5, pt: 1, pb: attachments.length ? 0.5 : 1.25 }}>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              display: "-webkit-box",
              WebkitLineClamp: 3,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
              fontSize: "0.85rem",
              lineHeight: 1.45,
              // Sem o teto explícito, navegador deixa vazar a sobra da 4ª linha.
              maxHeight: "calc(0.85rem * 1.45 * 3)",
            }}
          >
            {preview.slice(0, 280)}
          </Typography>
        </Box>
      )}

      {attachments.length > 0 && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, px: 1.5, pt: 0.5, pb: 1.25 }}>
          {attachments.map((file, index) => {
            const label = file.name || "Anexo";
            const href = file.url ? String(file.url) : "";
            return (
              <Box
                key={`${label}-${index}`}
                component={href ? "a" : "div"}
                href={href || undefined}
                target={href ? "_blank" : undefined}
                rel={href ? "noopener noreferrer" : undefined}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.75,
                  minWidth: 0,
                  color: "text.primary",
                  textDecoration: "none",
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: 1,
                  px: 1,
                  py: 0.5,
                  "&:hover": href ? { bgcolor: alpha(accent, 0.06) } : undefined,
                }}
              >
                <AttachFileIcon sx={{ fontSize: 16, color: "text.secondary" }} />
                <Typography variant="caption" sx={{ fontWeight: 600, wordBreak: "break-word" }}>
                  {label}
                </Typography>
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
};

export default OutboundEmailCard;
